import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  InputAdornment,
  IconButton,
  Divider,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  KeyboardReturn as EnterIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import { searchIndex } from "../utilities/searchIndex";

const GlobalSearch = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  const fuse = new Fuse(searchIndex, {
    keys: ["title", "category", "keywords"],
    threshold: 0.3,
    includeMatches: true,
  });

  useEffect(() => {
    if (query.trim() === "") {
      const grouped = {};
      searchIndex.forEach((item) => {
        if (!grouped[item.category]) grouped[item.category] = [];
        if (grouped[item.category].length < 2) grouped[item.category].push(item);
      });
      const initial = Object.values(grouped).flat();
      setResults(initial);
    } else {
      const fuseResults = fuse.search(query).map((r) => r.item);
      setResults(fuseResults);
    }
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery("");
    }
  }, [open]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      if (results[selectedIndex]) {
        handleSelect(results[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const handleSelect = (item) => {
    navigate(item.path);
    onClose();
  };

  const groupedResults = results.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const flattenedResults = [];
  Object.keys(groupedResults).forEach((category) => {
    groupedResults[category].forEach((item) => {
      flattenedResults.push(item);
    });
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        pt: "10vh",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          bgcolor: "background.default",
          borderRadius: 3,
          boxShadow: 24,
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
          outline: "none",
        }}
        onKeyDown={handleKeyDown}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <TextField
            fullWidth
            variant="standard"
            placeholder="Search pages, links, or content... (e.g. 'users', 'revenue')"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputRef={inputRef}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: { fontSize: "1.1rem" },
            }}
          />
        </Box>

        <Box sx={{ maxHeight: "60vh", overflowY: "auto" }} ref={resultsRef}>
          {results.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">No results found for "{query}"</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {Object.entries(groupedResults).map(([category, items], catIdx) => (
                <React.Fragment key={category}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="primary"
                      sx={{ textTransform: "uppercase", letterSpacing: 1 }}
                    >
                      {category}
                    </Typography>
                  </Box>
                  {items.map((item, itemIdx) => {
                    const overallIndex = results.indexOf(item);
                    const isSelected = selectedIndex === overallIndex;

                    return (
                      <ListItem key={item.path} disablePadding>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => handleSelect(item)}
                          sx={{
                            py: 1.5,
                            px: 3,
                            "&.Mui-selected": {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" fontWeight={isSelected ? 600 : 400}>
                                  {item.title}
                                </Typography>
                                {isSelected && (
                                  <Chip
                                    label="Enter"
                                    size="small"
                                    icon={<EnterIcon sx={{ fontSize: "14px !important" }} />}
                                    sx={{ height: 20, fontSize: "0.7rem", ml: "auto" }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {item.path} • {item.keywords.slice(0, 3).join(", ")}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  {catIdx < Object.entries(groupedResults).length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        <Box
          sx={{
            p: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                px: 0.5,
                borderRadius: 0.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.default",
                display: "flex",
                alignItems: "center",
              }}
            >
              <UpIcon sx={{ fontSize: 14 }} />
            </Box>
            <Box
              sx={{
                px: 0.5,
                borderRadius: 0.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.default",
                display: "flex",
                alignItems: "center",
              }}
            >
              <DownIcon sx={{ fontSize: 14 }} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              to navigate
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                px: 0.8,
                borderRadius: 0.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.default",
                fontSize: 10,
                fontWeight: "bold",
              }}
            >
              ESC
            </Box>
            <Typography variant="caption" color="text.secondary">
              to close
            </Typography>
          </Box>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Press
            </Typography>
            <Box
              sx={{
                px: 0.8,
                borderRadius: 0.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.default",
                fontSize: 10,
                fontWeight: "bold",
              }}
            >
              Ctrl + K
            </Box>
            <Typography variant="caption" color="text.secondary">
              to open
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default GlobalSearch;
